/**
 * RequestInfoCard Component
 * Read-only summary card for edit mode showing request type,
 * deadline and status, mirroring the overview of the request view page
 */

import React from 'react';
import { Badge } from '@dynamon-as/ui';
import { AccessTime, Today } from '@mui/icons-material';
import type { Request } from '@features/requests/types';
import {
    REQUEST_TYPE_ICONS,
    STATUS_BADGE_VARIANTS,
} from '@features/requests/constants';
import { formatDate, getRequestTypeKey } from '@features/requests/utils';
import { useI18n, useRequestTranslations } from '@shared/i18n';
import styles from './RequestInfoCard.module.css';

/**
 * Props for RequestInfoCard component
 */
export interface RequestInfoCardProps {
    /** The request to summarize */
    request: Partial<Request>;
}

/**
 * Summary card with request type, deadline and status
 */
export function RequestInfoCard({
    request,
}: RequestInfoCardProps): React.ReactElement {
    const { t } = useI18n();
    const { getRequestTypeLabel, getStatusLabel } = useRequestTranslations();

    const typeKey = request.type ? getRequestTypeKey(request.type) : undefined;
    const TypeIcon = typeKey ? REQUEST_TYPE_ICONS[typeKey] : undefined;
    const typeLabel = typeKey ? getRequestTypeLabel(typeKey) : '';
    const statusLabel = request.status ? getStatusLabel(request.status) : '';

    return (
        <div
            className={styles.card}
            data-testid="request-info-card"
        >
            <div className={styles.type}>
                {TypeIcon && (
                    <span className={styles.typeIcon}>
                        <TypeIcon fontSize="small" />
                    </span>
                )}
                <span className={styles.typeLabel}>{typeLabel}</span>
            </div>

            <div className={styles.details}>
                {request.deadline && (
                    <div className={styles.item}>
                        <span className={styles.itemIcon}>
                            <AccessTime fontSize="small" />
                        </span>
                        <div className={styles.itemContent}>
                            <span className={styles.itemLabel}>
                                {t('requests.details.deadline')}
                            </span>
                            <span className={styles.itemValue}>
                                {formatDate(request.deadline)}
                            </span>
                        </div>
                    </div>
                )}

                {request.status && (
                    <div className={styles.item}>
                        <span className={styles.itemIcon}>
                            <Today fontSize="small" />
                        </span>
                        <div className={styles.itemContent}>
                            <span className={styles.itemLabel}>
                                {t('requests.details.status')}
                            </span>
                            <Badge
                                text={statusLabel}
                                variant={STATUS_BADGE_VARIANTS[request.status]}
                                size="small"
                                dot
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default RequestInfoCard;
